import { useState, useEffect, useRef } from 'react';
import { Row, Col, Modal } from 'react-bootstrap';
import Swal from 'sweetalert2';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useCreateStepMutation, useUpdateStepMutation, useGetPhasesQuery } from '../../../Services/PathwayAdminAPI';
import '../../../Styles/RoleManagement.css';
import '../../../Styles/StepForm.css';

const StepForm = ({ show, onHide, step = null, onSuccess, preselectedPhaseId = null }) => {
  const [formData, setFormData] = useState({
    phase_id: '',
    title: '',
    step_number: 1,
    objective: '',
    pro_tip: '',
    reality_check_content: '',
    resident_review_content: '',
    key_actions: [''],
    quick_insight: '',
    tags: [],
    order_index: 0,
  });
  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState('');
  const [htmlMode, setHtmlMode] = useState({ pro_tip: false, reality_check_content: false, resident_review_content: false, objective: false });

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'color', 'background', 'link'
  ];

  const RichTextEditor = ({ field, value, onChange, placeholder, error }) => {
    const isHtml = htmlMode[field];
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
          <button
            type="button"
            onClick={() => setHtmlMode(prev => ({ ...prev, [field]: !prev[field] }))}
            style={{
              fontSize: '11px', padding: '2px 10px', border: '1px solid #CFD6E1',
              borderRadius: '4px', background: isHtml ? '#f0f0f0' : '#fff',
              cursor: 'pointer', color: '#555', fontFamily: 'monospace'
            }}
          >
            {isHtml ? 'Visual' : '&lt;/&gt; HTML'}
          </button>
        </div>
        {isHtml ? (
          <textarea
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="Enter raw HTML..."
            rows={10}
            style={{
              width: '100%', padding: '12px', borderRadius: '8px',
              border: '1px solid #CFD6E1', fontFamily: 'monospace',
              fontSize: '13px', resize: 'vertical',
            }}
          />
        ) : (
          <ReactQuill
            theme="snow"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            modules={quillModules}
            formats={quillFormats}
            style={{ backgroundColor: 'white', borderRadius: '8px' }}
            className={error ? 'is-invalid' : ''}
          />
        )}
      </div>
    );
  };

  const { data: phasesData } = useGetPhasesQuery();
  const [createStep, { isLoading: isCreating }] = useCreateStepMutation();
  const [updateStep, { isLoading: isUpdating }] = useUpdateStepMutation();

  const isEditMode = !!step;
  const isLoading = isCreating || isUpdating;
  const phases = phasesData?.data || [];

  useEffect(() => {
    if (!show) {
      setFormData({
        phase_id: '',
        title: '',
        step_number: 1,
        objective: '',
        pro_tip: '',
        reality_check_content: '',
        resident_review_content: '',
        key_actions: [''],
        quick_insight: '',
        tags: [],
        order_index: 0,
      });
      setErrors({});
      setTagInput('');
      return;
    }

    if (step) {
      setFormData({
        phase_id: step.phase_id || '',
        title: step.title || '',
        step_number: step.step_number || 1,
        objective: step.objective || '',
        pro_tip: step.pro_tip || '',
        reality_check_content: step.reality_check_content || '',
        resident_review_content: step.resident_review_content || '',
        key_actions: step.key_actions && step.key_actions.length > 0 ? step.key_actions : [''],
        quick_insight: step.quick_insight || '',
        tags: step.tags || [],
        order_index: step.order_index || 0,
      });
    } else {
      setFormData({
        phase_id: preselectedPhaseId || '',
        title: '',
        step_number: 1,
        objective: '',
        pro_tip: '',
        reality_check_content: '',
        resident_review_content: '',
        key_actions: [''],
        quick_insight: '',
        tags: [],
        order_index: 0,
      });
    }
    setErrors({});
    setTagInput('');
  }, [step, show, preselectedPhaseId]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.phase_id) {
      newErrors.phase_id = 'Phase is required';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Step title is required';
    } else if (formData.title.length > 500) {
      newErrors.title = 'Step title must not exceed 500 characters';
    }

    // Remove HTML tags for character count validation
    const stripHtml = (html) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };

    if (formData.objective && stripHtml(formData.objective).length > 1000) {
      newErrors.objective = 'Objective must not exceed 1000 characters';
    }
    
    if (formData.pro_tip && stripHtml(formData.pro_tip).length > 5000) {
      newErrors.pro_tip = 'Pro tip must not exceed 5000 characters (excluding HTML formatting)';
    }

    if (formData.reality_check_content && stripHtml(formData.reality_check_content).length > 5000) {
      newErrors.reality_check_content = 'Reality check content must not exceed 5000 characters (excluding HTML formatting)';
    }

    if (formData.quick_insight && formData.quick_insight.length > 500) {
      newErrors.quick_insight = 'Quick insight must not exceed 500 characters';
    }

    if (!formData.step_number || formData.step_number < 1) {
      newErrors.step_number = 'Step number must be at least 1';
    }

    if (formData.order_index < 0) {
      newErrors.order_index = 'Order index must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'order_index' || name === 'step_number' || name === 'phase_id' 
        ? parseInt(value) || (name === 'phase_id' ? '' : 0) 
        : value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleProTipChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      pro_tip: content,
    }));
    if (errors.pro_tip) {
      setErrors((prev) => ({ ...prev, pro_tip: '' }));
    }
  };

  const handleRealityCheckChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      reality_check_content: content,
    }));
    if (errors.reality_check_content) {
      setErrors((prev) => ({ ...prev, reality_check_content: '' }));
    }
  };

  const handleResidentReviewChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      resident_review_content: content,
    }));
    if (errors.resident_review_content) {
      setErrors((prev) => ({ ...prev, resident_review_content: '' }));
    }
  };

  const handleKeyActionChange = (index, value) => {
    const newKeyActions = [...formData.key_actions];
    newKeyActions[index] = value;
    setFormData((prev) => ({
      ...prev,
      key_actions: newKeyActions,
    }));
  };

  const addKeyAction = () => {
    setFormData((prev) => ({
      ...prev,
      key_actions: [...prev.key_actions, ''],
    }));
  };

  const removeKeyAction = (index) => {
    if (formData.key_actions.length > 1) {
      const newKeyActions = formData.key_actions.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        key_actions: newKeyActions,
      }));
    }
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData((prev) => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()],
        }));
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Filter out empty key actions
    const filteredKeyActions = formData.key_actions.filter((action) => action.trim() !== '');

    const submitData = {
      ...formData,
      key_actions: filteredKeyActions,
    };

    try {
      let response;
      if (isEditMode) {
        response = await updateStep({
          id: step.id,
          ...submitData,
        }).unwrap();
      } else {
        response = await createStep(submitData).unwrap();
      }

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: response.message || `Step ${isEditMode ? 'updated' : 'created'} successfully`,
        timer: 2000,
        showConfirmButton: false,
        confirmButtonColor: '#FD9C00',
      });

      if (onSuccess) {
        onSuccess(response.data);
      }
      onHide();
    } catch (error) {
      console.error('Error saving step:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} step`,
        confirmButtonColor: '#FD9C00',
      });
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Body>
        <h5>{isEditMode ? 'Edit Step' : 'Add New Step'}</h5>
        <form onSubmit={handleSubmit}>
          <Row>
            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>
                  Phase <span className="red-star">*</span>
                </label>
                <select
                  name="phase_id"
                  value={formData.phase_id}
                  onChange={handleChange}
                  className={errors.phase_id ? 'is-invalid' : ''}
                  disabled={isEditMode}
                >
                  <option value="">Select a phase</option>
                  {phases.map((phase) => (
                    <option key={phase.id} value={phase.id}>
                      Phase {phase.phase_number}: {phase.name}
                    </option>
                  ))}
                </select>
                {errors.phase_id && <p className="error-text">{errors.phase_id}</p>}
                {isEditMode && (
                  <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    Phase cannot be changed after creation
                  </small>
                )}
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>
                  Step Title <span className="red-star">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Enter step title (e.g., Define Your Cafe Business Concept & Model)"
                  className={errors.title ? 'is-invalid' : ''}
                  maxLength={500}
                />
                {errors.title && <p className="error-text">{errors.title}</p>}
              </div>
            </Col>

            <Col xs={12} sm={6} md={6} lg={6}>
              <div className="form-control">
                <label>
                  Step Number <span className="red-star">*</span>
                </label>
                <input
                  type="number"
                  name="step_number"
                  value={formData.step_number}
                  onChange={handleChange}
                  placeholder="Enter step number"
                  min={1}
                  className={errors.step_number ? 'is-invalid' : ''}
                />
                {errors.step_number && <p className="error-text">{errors.step_number}</p>}
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Step number for display (e.g., 1, 2, 3)
                </small>
              </div>
            </Col>

            <Col xs={12} sm={6} md={6} lg={6}>
              <div className="form-control">
                <label>
                  Order Index <span className="red-star">*</span>
                </label>
                <input
                  type="number"
                  name="order_index"
                  value={formData.order_index}
                  onChange={handleChange}
                  placeholder="Enter order index"
                  min={0}
                  className={errors.order_index ? 'is-invalid' : ''}
                />
                {errors.order_index && <p className="error-text">{errors.order_index}</p>}
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Lower numbers appear first
                </small>
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Objective</label>
                <RichTextEditor
                  field="objective"
                  value={formData.objective}
                  onChange={(content) => {
                    setFormData((prev) => ({ ...prev, objective: content }));
                    if (errors.objective) setErrors((prev) => ({ ...prev, objective: '' }));
                  }}
                  placeholder="Enter step objective (optional)"
                  error={errors.objective}
                />
                {errors.objective && <p className="error-text">{errors.objective}</p>}
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  {(() => { const d = document.createElement('div'); d.innerHTML = formData.objective; return (d.textContent || d.innerText || '').length; })()}/1000 characters
                </small>
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Pro Tip</label>
                <RichTextEditor
                  field="pro_tip"
                  value={formData.pro_tip}
                  onChange={handleProTipChange}
                  placeholder="Enter pro tip with formatting (optional)"
                  error={errors.pro_tip}
                />
                {errors.pro_tip && <p className="error-text">{errors.pro_tip}</p>}
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Use the toolbar to format your pro tip with bold, lists, colors, etc.
                </small>
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Reality Check Content</label>
                <RichTextEditor
                  field="reality_check_content"
                  value={formData.reality_check_content}
                  onChange={handleRealityCheckChange}
                  placeholder="Enter reality check content with formatting (optional). If set, the Reality Check shield icon will appear for this step."
                  error={errors.reality_check_content}
                />
                {errors.reality_check_content && <p className="error-text">{errors.reality_check_content}</p>}
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Leave empty to hide the Reality Check icon for this step. When set, users will see a pulsing shield icon until they open it.
                </small>
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Resident Review Content</label>
                <RichTextEditor
                  field="resident_review_content"
                  value={formData.resident_review_content}
                  onChange={handleResidentReviewChange}
                  placeholder="Enter resident review content with formatting (optional). If set, the Resident Review icon will appear for this step."
                  error={errors.resident_review_content}
                />
                {errors.resident_review_content && <p className="error-text">{errors.resident_review_content}</p>}
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Leave empty to hide the Resident Review icon for this step. When set, users will see a pulsing purple icon until they open it.
                </small>
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Key Actions</label>
                <div className="key-actions-list">
                  {formData.key_actions.map((action, index) => (
                    <div key={index} className="key-action-item">
                      <input
                        type="text"
                        value={action}
                        onChange={(e) => handleKeyActionChange(index, e.target.value)}
                        placeholder={`Key action ${index + 1}`}
                      />
                      {formData.key_actions.length > 1 && (
                        <button
                          type="button"
                          className="btn-remove-action"
                          onClick={() => removeKeyAction(index)}
                          title="Remove action"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn-add-action"
                    onClick={addKeyAction}
                  >
                    <i className="fas fa-plus"></i> Add Key Action
                  </button>
                </div>
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Quick Insight</label>
                <textarea
                  name="quick_insight"
                  value={formData.quick_insight}
                  onChange={handleChange}
                  placeholder="Enter quick insight (optional)"
                  rows={3}
                  className={errors.quick_insight ? 'is-invalid' : ''}
                  maxLength={500}
                  style={{
                    width: '100%',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #CFD6E1',
                    fontFamily: 'Open Sans',
                    fontSize: '14px',
                    resize: 'vertical',
                  }}
                />
                {errors.quick_insight && <p className="error-text">{errors.quick_insight}</p>}
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  {formData.quick_insight.length}/500 characters
                </small>
              </div>
            </Col>

            <Col xs={12} sm={12} md={12} lg={12}>
              <div className="form-control">
                <label>Tags</label>
                <div className="tags-container">
                  <div className="tags-list">
                    {formData.tags.map((tag, index) => (
                      <span key={index} className="tag-item">
                        {tag}
                        <button
                          type="button"
                          className="btn-remove-tag"
                          onClick={() => removeTag(tag)}
                          title="Remove tag"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagInputKeyDown}
                    placeholder="Type a tag and press Enter"
                    className="tag-input"
                  />
                </div>
                <small style={{ color: '#6c757d', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Press Enter to add a tag
                </small>
              </div>
            </Col>
          </Row>
          <Row>
            <div className="btn-action">
              <button
                type="button"
                className="btn-outline-black"
                onClick={onHide}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={isLoading ? 'btn-primary-disabled' : 'btn-primary'}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </Row>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default StepForm;
